export const EMPTY_FILTERS = {
  fechaDesde: "",
  fechaHasta: "",
  tipoPasarela: "",
  tipoFuente: "",
  comercio: "",
  pasarela: "",
  servicio: "",
};
export const TABLE_LIMIT = 10000;
const number = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
export function calendarDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  if (!match) return false;
  const [, year, month, day] = match.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}
export function recordDay(value) {
  if (!value) return "";
  const text = String(value);
  if (!calendarDate(text.slice(0, 10))) return "";
  if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(text)) return text.slice(0, 10);
  const date = new Date(text);
  if (!Number.isFinite(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Bogota",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  return ["year", "month", "day"]
    .map((type) => parts.find((part) => part.type === type).value)
    .join("-");
}
export function validateFilters(filters) {
  if ([filters.fechaDesde, filters.fechaHasta].some((value) => value && !calendarDate(value)))
    return "Selecciona fechas válidas.";
  if (filters.fechaDesde && filters.fechaHasta && filters.fechaDesde > filters.fechaHasta)
    return "La fecha de inicio no puede ser posterior a la fecha final.";
  return "";
}
export function counts(item, tipo) {
  return tipo === "aceptacion"
    ? {
        total: number(item.total_transacciones_pasarela_aceptacion),
        success: number(item.cantidad_exitosa_pasarela_aceptacion),
        failed: number(item.cantidad_no_exitosa_pasarela_aceptacion),
        amount: number(item.valor_trns),
      }
    : {
        total: number(item.cantidad_transacciones),
        success: number(item.cantidad_exitosa_pasarela),
        failed: number(item.cantidad_no_exitosa_pasarela),
        amount: number(item.valor),
      };
}
export const effectiveness = (success, total) => (total > 0 ? (success / total) * 100 : null);
export function metrics(records, tipo) {
  const result = records.reduce(
    (sum, item) => {
      const value = counts(item, tipo);
      return {
        totalTransacciones: sum.totalTransacciones + value.total,
        transaccionesExitosas: sum.transaccionesExitosas + value.success,
        transaccionesFallidas: sum.transaccionesFallidas + value.failed,
        montoTotal: sum.montoTotal + value.amount,
      };
    },
    { totalTransacciones: 0, transaccionesExitosas: 0, transaccionesFallidas: 0, montoTotal: 0 }
  );
  return {
    ...result,
    efectividadGlobal: effectiveness(result.transaccionesExitosas, result.totalTransacciones),
    totalRegistros: records.length,
  };
}
export function applyFilters(records, filters, tipo) {
  return records.filter((item) => {
    const day = recordDay(item.fecha);
    if ((filters.fechaDesde || filters.fechaHasta) && !day) return false;
    if (filters.fechaDesde && day < filters.fechaDesde) return false;
    if (filters.fechaHasta && day > filters.fechaHasta) return false;
    const fields = [
      "comercio",
      "pasarela",
      "servicio",
      ...(tipo === "recaudo" ? ["tipoFuente"] : ["tipoPasarela"]),
    ];
    return fields.every(
      (field) =>
        !filters[field] ||
        String(
          item[
            field === "tipoFuente"
              ? "tipo_fuente"
              : field === "tipoPasarela"
              ? "tipopasarela"
              : field
          ] ?? ""
        ) === filters[field]
    );
  });
}
export function filterOptions(records) {
  return Object.fromEntries(
    Object.entries({
      comercios: "comercio",
      pasarelas: "pasarela",
      servicios: "servicio",
      tiposPasarela: "tipopasarela",
      tiposFuente: "tipo_fuente",
    }).map(([key, field]) => [
      key,
      [
        ...new Set(
          records
            .map((item) => item[field])
            .filter((value) => value != null && value !== "")
            .map(String)
        ),
      ].sort((a, b) => a.localeCompare(b, "es")),
    ])
  );
}
export function tableRecords(records, search = "", limit = TABLE_LIMIT) {
  const term = search.trim().toLocaleLowerCase("es");
  const matched = records.filter(
    (item) =>
      !term ||
      Object.values(item).some(
        (value) =>
          typeof value !== "object" &&
          String(value ?? "")
            .toLocaleLowerCase("es")
            .includes(term)
      )
  );
  return {
    total: matched.length,
    rows: [...matched]
      .sort(
        (a, b) =>
          recordDay(b.fecha).localeCompare(recordDay(a.fecha)) ||
          (Date.parse(b.fecha) || 0) - (Date.parse(a.fecha) || 0)
      )
      .slice(0, limit),
  };
}
export function aggregateEffectiveness(records, tipo, field) {
  const groups = new Map();
  records.forEach((item) => {
    const label =
      field === "fecha" ? recordDay(item.fecha) : String(item[field] || "Sin información");
    if (!label) return;
    const sum = groups.get(label) || { label, total: 0, success: 0 };
    const value = counts(item, tipo);
    sum.total += value.total;
    sum.success += value.success;
    groups.set(label, sum);
  });
  return Array.from(groups.values(), (group) => ({
    ...group,
    value: effectiveness(group.success, group.total),
  })).sort((a, b) =>
    field === "fecha"
      ? a.label.localeCompare(b.label)
      : b.total - a.total || a.label.localeCompare(b.label)
  );
}
export const formatAmount = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
export const formatCount = (value) => new Intl.NumberFormat("es-CO").format(value);
export const formatPercent = (value) =>
  value == null
    ? "Sin transacciones"
    : `${value.toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
